import { getTableData } from '@utility/indexedDB_utils';

export const getWorkoutHistory = async () => {
  try {
    const sessions = await getTableData('workout_sessions');
    const logs = await getTableData('workout_log');
    const sets = await getTableData('sets');
    const exercises = await getTableData('exercises');

    return sessions.map(session => ({
      ...session,
      exercises: logs
        .filter(log => log.session_id === session.id)
        .map(log => {
          const exercise = exercises.find(ex => ex.id === log.exercise_id);
          return {
            ...log,
            exercise_name: exercise?.name || 'Unknown',
            sets: sets.filter(set => set.workout_log_id === log.id)
          };
        })
    }));
  } catch (error) {
    console.error('Error fetching workout history:', error);
    throw new Error('Failed to fetch workout history');
  }
};

export const getMuscleDistribution = async (
  muscleMode = 'total', 
  useExact = false,
  startDate = null,
  endDate = null
) => {
  try {
    const muscles = await getTableData('muscles');
    const muscleGroups = await getTableData('muscle_groups');
    const muscleToGroup = await getTableData('muscle_to_group');
    const exercises = await getTableData('exercises');
    const primaryMuscles = await getTableData('exercise_primary_muscles');
    const secondaryMuscles = await getTableData('exercise_secondary_muscles');
    const sessions = await getTableData('workout_sessions');
    const logs = await getTableData('workout_log');
    const sets = await getTableData('sets');

    // Filter sessions by date range if provided
    const filteredSessions = sessions.filter(session => {
      if (!startDate && !endDate) return true;
      
      // Parse session date (ISO format: "2025-05-28T11:28:51.683Z")
      const sessionDate = new Date(session.session_date);
      
      // Convert string dates to Date objects if needed
      const start = startDate ? (typeof startDate === 'string' ? new Date(startDate) : startDate) : null;
      const end = endDate ? (typeof endDate === 'string' ? new Date(endDate) : endDate) : null;
      
      // Normalize dates to beginning/end of day for comparison
      if (start) {
        start.setHours(0, 0, 0, 0);
      }
      if (end) {
        end.setHours(23, 59, 59, 999);
      }
      
      const isAfterStart = !start || sessionDate >= start;
      const isBeforeEnd = !end || sessionDate <= end;
      
      return isAfterStart && isBeforeEnd;
    });

    // Get only logs from filtered sessions
    const sessionIds = filteredSessions.map(s => s.id);
    const filteredLogs = logs.filter(log => sessionIds.includes(log.session_id));

    // Create a map of muscle_id to group info
    const muscleGroupMap = {};
    muscleToGroup.forEach(mg => {
      const group = muscleGroups.find(g => g.id === mg.group_id);
      if (group) {
        muscleGroupMap[mg.muscle_id] = {
          group_id: mg.group_id,
          group_name: group.name
        };
      }
    });

    return muscles.map(muscle => {
      let volume = 0;
      let reps = 0;
      let setsCount = 0;
      
      // Process primary muscle connections
      if (muscleMode === 'primary' || muscleMode === 'total') {
        const primaryConnections = primaryMuscles.filter(pm => pm.muscle_id === muscle.id);
        
        primaryConnections.forEach(conn => {
          const exerciseId = conn.exercise_id;
          const percent = useExact ? (conn.percent / 100) : 1;
          
          const exerciseLogs = filteredLogs.filter(log => log.exercise_id === exerciseId);
          
          exerciseLogs.forEach(log => {
            const exerciseSets = sets.filter(set => set.workout_log_id === log.id);
            
            exerciseSets.forEach(set => {
              if (set.weight && set.reps) {
                volume += set.weight * set.reps * percent;
                reps += set.reps * percent;
                setsCount += 1 * percent;
              }
            });
          });
        });
      }

      // Process secondary muscle connections
      if (muscleMode === 'secondary' || muscleMode === 'total') {
        const secondaryConnections = secondaryMuscles.filter(sm => sm.muscle_id === muscle.id);
        
        secondaryConnections.forEach(conn => {
          const exerciseId = conn.exercise_id;
          const percent = useExact ? (conn.percent / 100) : 0.5;
          
          const exerciseLogs = filteredLogs.filter(log => log.exercise_id === exerciseId);
          
          exerciseLogs.forEach(log => {
            const exerciseSets = sets.filter(set => set.workout_log_id === log.id);
            
            exerciseSets.forEach(set => {
              if (set.weight && set.reps) {
                volume += set.weight * set.reps * percent;
                reps += set.reps * percent;
                setsCount += 1 * percent;
              }
            });
          });
        });
      }

      return {
        muscle: muscle.name,
        muscle_id: muscle.id,
        group_id: muscleGroupMap[muscle.id]?.group_id || null,
        group_name: muscleGroupMap[muscle.id]?.group_name || 'Unknown Group',
        volume: Math.round(volume * 100) / 100,
        reps: Math.round(reps * 100) / 100,
        sets: Math.round(setsCount * 100) / 100
      };
    }).filter(muscle => muscle.volume > 0 || muscle.reps > 0 || muscle.sets > 0);
  } catch (error) {
    console.error('Error fetching muscle distribution:', error);
    throw new Error('Failed to fetch muscle distribution data');
  }
};

export const getExerciseStats = async () => {
  try {
    const exercises = await getTableData('exercises');
    const logs = await getTableData('workout_log');
    const sets = await getTableData('sets');

    return exercises.map(exercise => {
      const exerciseLogs = logs.filter(log => log.exercise_id === exercise.id);
      const exerciseSets = sets.filter(set => 
        exerciseLogs.some(log => log.id === set.workout_log_id)
      );

      if (exerciseSets.length === 0) return null;

      const validSets = exerciseSets.filter(set => set.weight && set.reps);
      
      if (validSets.length === 0) return null;

      const maxWeight = Math.max(...validSets.map(set => set.weight));
      const maxReps = Math.max(...validSets.map(set => set.reps));
      const maxVolume = Math.max(...validSets.map(set => set.weight * set.reps));
      const totalVolume = validSets.reduce((sum, set) => sum + (set.weight * set.reps), 0);
      const totalReps = validSets.reduce((sum, set) => sum + set.reps, 0);
      const totalSets = validSets.length;

      return {
        exercise: exercise.name,
        exercise_id: exercise.id,
        maxWeight: Math.round(maxWeight * 100) / 100,
        maxReps,
        maxVolume: Math.round(maxVolume * 100) / 100,
        totalVolume: Math.round(totalVolume * 100) / 100,
        totalReps,
        totalSets,
        avgWeight: Math.round((totalVolume / totalReps) * 100) / 100,
        avgReps: Math.round((totalReps / totalSets) * 100) / 100,
        frequency: exerciseLogs.length // Number of times exercise was performed
      };
    }).filter(Boolean).sort((a, b) => b.totalVolume - a.totalVolume); // Sort by total volume descending
  } catch (error) {
    console.error('Error fetching exercise stats:', error);
    throw new Error('Failed to fetch exercise statistics');
  }
};

export const getOverallStats = async () => {
  try {
    const sets = await getTableData('sets');
    const sessions = await getTableData('workout_sessions');
    
    const validSets = sets.filter(set => set.weight && set.reps);
    
    if (validSets.length === 0) {
      return {
        totalWeight: 0,
        totalReps: 0,
        totalSets: 0,
        totalVolume: 0,
        avgWeightPerSet: 0,
        avgRepsPerSet: 0,
        avgVolumePerSet: 0,
        totalWorkouts: sessions.length
      };
    }

    const totalWeight = validSets.reduce((sum, set) => sum + set.weight, 0);
    const totalReps = validSets.reduce((sum, set) => sum + set.reps, 0);
    const totalSets = validSets.length;
    const totalVolume = validSets.reduce((sum, set) => sum + (set.weight * set.reps), 0);

    return {
      totalWeight: Math.round(totalWeight * 100) / 100,
      totalReps,
      totalSets,
      totalVolume: Math.round(totalVolume * 100) / 100,
      avgWeightPerSet: Math.round((totalWeight / totalSets) * 100) / 100,
      avgRepsPerSet: Math.round((totalReps / totalSets) * 100) / 100,
      avgVolumePerSet: Math.round((totalVolume / totalSets) * 100) / 100,
      totalWorkouts: sessions.length
    };
  } catch (error) {
    console.error('Error fetching overall stats:', error);
    throw new Error('Failed to fetch overall statistics');
  }
};

// Additional utility function for muscle group analysis
export const getMuscleGroupDistribution = async (muscleMode = 'total', useExact = false) => {
  try {
    const muscleData = await getMuscleDistribution(muscleMode, useExact);
    const groupMap = {};

    muscleData.forEach(muscle => {
      const groupId = muscle.group_id;
      const groupName = muscle.group_name;

      if (!groupMap[groupId]) {
        groupMap[groupId] = {
          group_name: groupName,
          group_id: groupId,
          volume: 0,
          reps: 0,
          sets: 0,
          muscle_count: 0
        };
      }

      groupMap[groupId].volume += muscle.volume || 0;
      groupMap[groupId].reps += muscle.reps || 0;
      groupMap[groupId].sets += muscle.sets || 0;
      groupMap[groupId].muscle_count += 1;
    });

    return Object.values(groupMap)
      .map(group => ({
        ...group,
        volume: Math.round(group.volume * 100) / 100,
        reps: Math.round(group.reps * 100) / 100,
        sets: Math.round(group.sets * 100) / 100
      }))
      .sort((a, b) => b.volume - a.volume);
  } catch (error) {
    console.error('Error fetching muscle group distribution:', error);
    throw new Error('Failed to fetch muscle group distribution');
  }
};